import { ArrowRight } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Banner } from '@/components/feedback/Banner'
import { EmptyState } from '@/components/feedback/EmptyState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { useSession } from '@/features/auth/api/useSession'
import { toQuotePayload, useCreateQuote } from '@/features/quotes/api/useQuote'
import { QuoteContextCard } from '@/features/quotes/components/QuoteAsideCards'
import { QuoteIdentityStep } from '@/features/quotes/components/QuoteIdentityStep'
import {
  QuotePage,
  QuotePageColumns,
  QuotePageHeader,
} from '@/features/quotes/components/QuotePageChrome'
import { errorMessage } from '@/features/quotes/quoteText'
import {
  EMPTY_QUOTE_DRAFT,
  quoteDraftInvalid,
  todayDateOnly,
  type QuoteDraft,
} from '@/features/quotes/quoteDraft'
import { can, CAP } from '@/lib/can'

/* OPENING A QUOTE: one screen, one question — who is this for (FR-4, FR-5).
 *
 * ── What this replaced ──────────────────────────────────────────────────────
 *
 * A three-step builder at /quotes/new + /quotes/:id/build, which asked for the
 * client, then the routing and cargo, then the costing and the terms, with a
 * step rail and a Next button between them. Every one of those steps had a
 * TWIN on the detail page — the same cargo table, the same cost editor, the
 * same route options, the same terms — because both surfaces hosted the same
 * `QuoteSections` components. So the product had two answers to "where do I
 * add a cost line", the builder was reachable for exactly one visit unless you
 * found the button that led back into it, and a reader who learned one screen
 * had to learn the other.
 *
 * The header fields the builder's disclosures offered were never unique to it
 * either: `QuoteFormSheet` behind the detail page's Edit button asks all
 * twenty-one of them, and always did. The disclosures were a second door onto
 * one form.
 *
 * So the builder is gone and this is what is left of its first step: the
 * questions that must be answered BEFORE a record can exist, because they are
 * what the create payload carries. Everything else is answered on the record
 * itself, on the tab that owns it.
 *
 * ── Why the create is still its own screen ──────────────────────────────────
 *
 * Because a quote cannot be opened without a client (FR-4) and the number is
 * minted server-side on the insert (FR-5, AD-12). There is no record to hang a
 * tab off until this form is submitted. That is the whole justification for a
 * separate route, and it is why this page asks SIX questions rather than
 * twenty-one: anything that can be answered later is answered later.
 *
 * ── Where it goes ───────────────────────────────────────────────────────────
 *
 * Straight to /quotes/:id, `replace` rather than a push. The empty /quotes/new
 * is not somewhere the back button should return to: going back to it would
 * offer to create a SECOND quote from a header that has already been used.
 */
export function QuoteCreatePage() {
  const { t } = useTranslation()
  /* Refused BEFORE any input (#128, N-07). The create is decided by
     `quote.create` alone on the server, and a reader without it — Operations,
     Finance, a founder's dispatcher role — reached this page by a deep link,
     picked a client and met a 403 on «Создать котировку». So without the key
     there is no form at all: no client picker, so no directory read either.
     The capability, never a role name; the server re-checks (AD-21). While
     the session loads a skeleton stands in, so the refusal never flashes at a
     reader who may create. */
  const { data: session, isPending: sessionPending } = useSession()

  if (sessionPending) {
    return (
      <>
        <PageHeader title={t('quotes.create.title')} />
        <Skeleton className="h-64 rounded-[var(--radius-DEFAULT)]" />
      </>
    )
  }

  if (!can(session, CAP.quoteCreate)) {
    return (
      <>
        <PageHeader title={t('quotes.create.title')} />
        <Card>
          <EmptyState
            title={t('quotes.create.noAccess.title')}
            description={t('quotes.create.noAccess.description')}
          />
        </Card>
      </>
    )
  }

  return <QuoteCreateForm />
}

function QuoteCreateForm() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [draft, setDraft] = useState<QuoteDraft>({
    ...EMPTY_QUOTE_DRAFT,
    quoteDate: todayDateOnly(),
  })
  /** Errors stay quiet until a submit has been attempted: a form that reddens
   *  while it is being filled in is scolding somebody mid-sentence. */
  const [touched, setTouched] = useState(false)

  const create = useCreateQuote()

  const submit = () => {
    setTouched(true)
    if (quoteDraftInvalid(draft)) return
    create.mutate(toQuotePayload(draft), {
      onSuccess: (quote) => navigate(`/quotes/${quote.id}`, { replace: true }),
    })
  }

  return (
    <QuotePage>
      <QuotePageHeader
        backTo="/quotes"
        backLabel={t('quotes.backToList')}
        /* The ACT is the title here, not a number: there is no record yet, and
           the detail page this lands on titles itself with the number the
           server mints. */
        title={t('quotes.create.title')}
        subtitle={t('quotes.create.subtitle')}
        trailing={
          <Button
            iconRight={ArrowRight}
            disabled={create.isPending}
            onClick={submit}
          >
            {create.isPending
              ? t('common.form.saving')
              : t('quotes.actions.create')}
          </Button>
        }
      />

      {/* What the record IS, beside the form that is about to create it. The
          number is the one line on it that does not exist yet, and saying so is
          the point: FR-5 mints it server-side, so somebody who expects to
          choose one finds out here rather than by hunting for the field. */}
      <QuotePageColumns aside={<QuoteContextCard />}>
        {create.error ? (
          <Banner tone="negative" title={errorMessage(create.error, t)} />
        ) : null}

        <QuoteIdentityStep draft={draft} setDraft={setDraft} touched={touched} />
      </QuotePageColumns>
    </QuotePage>
  )
}
